import React, { useCallback, useEffect, useMemo } from 'react'
import './order-list.scss'
import { Column, Row } from '@/components/layout/layout'
import useBemClass from '@/utils/hooks/useBEMClass'
import { useRequest } from 'ahooks'
import { $post } from '@/api'
import { Pagination, Spin, Empty } from 'antd'
import { IMallOrder } from '../../type'
import { get, errorCallBack } from '@/utils'
import Iconfont from '@/components/iconfont/iconfont'
import { mallOrderTypeTag } from '../order-table/order-table'
import RefundApply from './image/refund-apply.svg'
import Chulizhong from './image/chulizhong.svg'
import Daichuli from './image/daichuli.svg'
import Daibukuan from './image/daibukuan.svg'
import { showLayer } from '@/utils/layer'
import MallOrderDialog from '../../order-dialog/order-dialog'
import MallOrderDetail from '../order-detail/order-detail'
import { mallOrderFilterStore } from '../../order-filter/filter-store'
import { observer } from 'mobx-react'
import { mallOrderStore } from '../../store'
import WordHighLight from '@/components/word-highlight/word-highlight'
import locationImg from '@/assets/images/location.svg'

const mallOrderListTag: Record<string, string> = {
    '2': Daichuli,
    '3': Chulizhong,
    '4': RefundApply,
    '5': Daibukuan,
}

const isRefundedOrder = (order: IMallOrder.OnlineOrder) => [2, 4].includes(order.order_info.refund_status)

interface OrderListProps {}

const MallOrderList: React.FC<OrderListProps> = props => {
    const cls = useBemClass('mall-order-list')

    const handle = useRequest<IMallOrder.OnlineOrderData>(
        (page?: number) => {
            const status = (mallOrderFilterStore.filterQuery.status_list || [1, 2, 4, 10]).filter(
                s => ![3, 6].includes(s),
            )

            return status.length
                ? $post({
                      url: '/mall/getshoponlineorders',
                      params: {
                          limit: 4,
                          page: page || 1,
                          from_date: mallOrderFilterStore.date.length ? mallOrderFilterStore.date[0] : void 0,
                          to_date: mallOrderFilterStore.date.length ? mallOrderFilterStore.date[1] : void 0,
                          keyword: mallOrderFilterStore.keyWord || void 0,
                          ...mallOrderFilterStore.filterQuery,
                          status_list: status,
                      },
                  })
                : new Promise(resolve => {
                      resolve({
                          can_bulk_order_nos: [],
                          orders: [],
                          pagination: { page: 1, page_count: 1, limit: 4 },
                      })
                  })
        },
        {
            refreshDeps: [mallOrderStore.orderListRefresh],
            onError: errorCallBack,
        },
    )

    useEffect(() => {
        handle.run(1)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mallOrderFilterStore.filterMap, mallOrderFilterStore.date, mallOrderFilterStore.keyWord])

    const getGoodName = useCallback((name: string[]) => {
        if (name.length > 2) {
            return `${name.slice(0, 2).join('、')}...等${name.length}种`
        } else {
            return name.join('、')
        }
    }, [])

    const getTag = useCallback((r: IMallOrder.OnlineOrder) => {
        return get(mallOrderTypeTag, r.has_seckill_goods ? '5' : `${r.order_info.order_type}`, '')
    }, [])

    const visibleOrders = useMemo(() => {
        return (handle.data?.orders || []).filter(order => !isRefundedOrder(order))
    }, [handle.data])

    const isOnlyFinish = useMemo(() => {
        return !visibleOrders.length
    }, [visibleOrders])

    return (
        <Column data-margin-h-20 data-margin-v-26>
            <Row data-align-items-center data-justify-content-space-between>
                <div className={cls('title')}>未完成</div>
                {!isOnlyFinish && (
                    <Row data-align-items-center>
                        <div
                            className="action"
                            data-margin-right-8
                            onClick={() => {
                                showLayer(<MallOrderDialog />)
                            }}
                        >
                            查看全部
                        </div>
                        <Pagination
                            size="small"
                            total={
                                get(handle.data, 'pagination.limit', 1) * get(handle.data, 'pagination.page_count', 1)
                            }
                            hideOnSinglePage
                            showQuickJumper
                            showSizeChanger={false}
                            defaultPageSize={4}
                            onChange={page => {
                                handle.run(page)
                            }}
                        />
                    </Row>
                )}
            </Row>
            <Spin spinning={handle.loading}>
                {isOnlyFinish ? (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                ) : (
                    <Row>
                        {visibleOrders.map(order => (
                            <div
                                className={cls('card')}
                                key={order.order_info.order_no}
                                onClick={() => {
                                    showLayer(
                                        <MallOrderDetail
                                            orderId={order.order_info.order_id}
                                            orderType={order.order_info.order_type}
                                        />,
                                    )
                                }}
                            >
                                <img
                                    src={mallOrderListTag[`${order.order_info.status}`]}
                                    className={cls('card', 'tag')}
                                    alt=""
                                />
                                <Row data-justify-content-space-between>
                                    {order.order_info.delivery_type === 0 &&
                                    order.order_info.self_pickup_site?.address ? (
                                        <Row data-align-items-center>
                                            <img className={cls('avatar')} src={locationImg} alt="" />
                                            <Column>
                                                <div className={cls('nickname')}>
                                                    {order.order_info.self_pickup_site?.name}
                                                </div>
                                                <div className={cls('realname')}>{order.order_info.create_time}</div>
                                            </Column>
                                        </Row>
                                    ) : (
                                        <Row data-align-items-center>
                                            <img
                                                style={{
                                                    border: `1px solid ${
                                                        order.order_info.customer.gender === 2 ? '#ee5b5b' : '#3c90ff'
                                                    }`,
                                                }}
                                                className={cls('avatar')}
                                                src={order.order_info.customer.avatar}
                                                alt=""
                                            />
                                            <Column>
                                                <WordHighLight
                                                    classes={cls('nickname')}
                                                    keyword={mallOrderFilterStore.keyWord}
                                                    word={order.order_info.customer.nickname}
                                                ></WordHighLight>
                                                <div className={cls('realname')}>{order.order_info.create_time}</div>
                                            </Column>
                                        </Row>
                                    )}

                                    <Row data-align-items-center>
                                        <Iconfont
                                            name={order.order_info.delivery_type === 0 ? 'icon13ziti' : 'icon13peisong'}
                                            size={20}
                                        />
                                        <span data-color-000 data-font-size-14 data-font-weight-600 data-margin-left-4>
                                            {order.order_info.delivery_type === 0 ? '自提' : '配送'}
                                        </span>
                                    </Row>
                                </Row>
                                <Row data-align-items-center>
                                    {getTag(order) ? <img data-margin-right-6 src={getTag(order)} alt="" /> : ''}
                                    <div
                                        data-color-000
                                        data-font-size-14
                                        data-font-weight-600
                                        className={cls('good-name')}
                                    >
                                        {getGoodName(order.order_goods_names)}
                                    </div>
                                </Row>
                            </div>
                        ))}
                    </Row>
                )}
            </Spin>
        </Column>
    )
}

export default observer(MallOrderList)
